import { useCallback } from 'react';

import { useParams, generatePath } from 'react-router-dom';

import { useDashboardPath } from './useDashboardPath';

/**
 * Configuration for handling checkout completion redirects
 * Each service can have either a static URL string or a function that receives orderUuid
 */
const CHECKOUT_COMPLETE_REDIRECT_CONFIG = {
  llc_annual_report: {
    target: 'external',
    basePath: '/journeys/annual-report-filing/:businessEntityUuid'
  },
  trademarks_search: {
    basePath: '/compliance/trademarks'
  },
  beneficial_ownership_filing: {
    target: 'external',
    basePath: '/f/go/complete_beneficial_ownership_filing',
    options: {
      idAlias: 'businessEntityUuid',
      orderIdAlias: 'orderUuid'
    }
  },
  domain_name_reg: {
    target: 'external',
    basePath: '/r/domainname-registration-v2/init',
    options: {
      idAlias: 'companyId',
      orderIdAlias: 'orderUuid'
    }
  },
  zb_tax_advice: {
    basePath: '/money/tax-services'
  },
  zb_tax_advice_biztax_prep: {
    basePath: '/money/tax-services'
  },
  llc_amendment: {
    target: 'external',
    basePath: '/journeys/llc-amendment/:businessEntityUuid'
  },
  ein_creation: {
    target: 'external',
    basePath: '/journeys/ein-fulfillment/:businessEntityUuid'
  }
};

export default function useCheckoutComplete() {
  const { businessEntityUuid } = useParams();
  const dashboardPath = useDashboardPath();

  const getCheckoutCompleteUrl = useCallback(
    (serviceCode, orderUuid = null) => {
      const redirectConfig = CHECKOUT_COMPLETE_REDIRECT_CONFIG[serviceCode];

      if (!redirectConfig) {
        return null;
      }

      const { target, basePath, options = {} } = redirectConfig;

      if (target === 'external') {
        const { idAlias, orderIdAlias, ...data } = options;
        const path = generatePath(basePath, { businessEntityUuid });
        const url = new URL(path, window.location);
        const searchParams = new URLSearchParams();

        Object.entries(data).forEach(([key, value]) => {
          searchParams.set(key, value);
          url.search = searchParams.toString();
        });

        if (orderIdAlias && orderUuid) {
          searchParams.set(orderIdAlias, orderUuid);
          url.search = searchParams.toString();
        }

        if (idAlias) {
          searchParams.set(idAlias, businessEntityUuid);
          url.search = searchParams.toString();
        }

        return decodeURIComponent(String(url));
      }

      return `${dashboardPath}${basePath}`;
    },
    [businessEntityUuid, dashboardPath]
  );

  const handleCheckoutComplete = useCallback(
    (serviceCode, orderUuid = null) => {
      const url = getCheckoutCompleteUrl(serviceCode, orderUuid);

      if (!url) {
        // Default behavior: reload the page to refresh entitlements
        window.location.reload();
        return;
      }

      window.location.href = url;
    },
    [getCheckoutCompleteUrl]
  );

  return {
    handleCheckoutComplete,
    getCheckoutCompleteUrl
  };
}
